import React, { useState } from 'react';
import PropTypes from 'prop-types';
import PlanDetailForm from './PlanDetailForm';
import Icons from '../svgs/icons';

const PlanDetailsManagement = ({
  title = 'Plan Details',
  planDetails,
  onChange,
  disabled = false,
  placeholder = 'No plan details added yet'
}) => {
  const [expandedPlanIndex, setExpandedPlanIndex] = useState(null);

  const handleAddPlanDetail = () => {
    const newPlanDetail = {
      title: '',
      description: '',
      icon: ''
    };

    const updatedPlanDetails = [...planDetails, newPlanDetail];
    onChange(updatedPlanDetails);

    // Auto-expand the newly added plan detail
    setExpandedPlanIndex(updatedPlanDetails.length - 1);
  };

  const handlePlanDetailChange = (index, field, value) => {
    const updatedPlanDetails = [...planDetails];
    updatedPlanDetails[index] = {
      ...updatedPlanDetails[index],
      [field]: value
    };
    onChange(updatedPlanDetails);
  };

  const handleRemovePlanDetail = (index) => {
    const updatedPlanDetails = planDetails.filter((_, i) => i !== index);
    onChange(updatedPlanDetails);

    // Reset expanded state if removing the expanded plan detail
    if (expandedPlanIndex === index) {
      setExpandedPlanIndex(null);
    } else if (expandedPlanIndex !== null && expandedPlanIndex > index) {
      setExpandedPlanIndex(expandedPlanIndex - 1);
    }
  };

  const handleToggleExpand = (index) => {
    setExpandedPlanIndex(expandedPlanIndex === index ? null : index);
  };

  const getPlanDetailsSummary = () => {
    const totalDetails = planDetails.length;
    const completedDetails = planDetails.filter(
      (detail) => detail.title?.trim() && detail.description?.trim()
    ).length;

    return { totalDetails, completedDetails };
  };

  const { totalDetails, completedDetails } = getPlanDetailsSummary();

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
          {totalDetails > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {completedDetails} of {totalDetails} plan details completed
            </p>
          )}
        </div>

        {!disabled && (
          <button
            onClick={handleAddPlanDetail}
            className="btn-primary"
          >
            <Icons.plusCircle className="w-4 h-4" />
            Add Plan Detail
          </button>
        )}
      </div>

      {/* Plan Details List */}
      <div className="space-y-4">
        {planDetails.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icons.documentText className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">{placeholder}</p>
            {!disabled && (
              <button
                onClick={handleAddPlanDetail}
                className="mt-4 text-shoorah-primary hover:text-shoorah-secondary text-sm font-medium"
              >
                Add your first plan detail
              </button>
            )}
          </div>
        ) : (
          planDetails.map((planDetail, index) => (
            <PlanDetailForm
              key={index}
              planDetail={planDetail}
              index={index}
              onChange={handlePlanDetailChange}
              onRemove={handleRemovePlanDetail}
              disabled={disabled}
              isExpanded={expandedPlanIndex === index}
              onToggleExpand={handleToggleExpand}
            />
          ))
        )}
      </div>

      {/* Summary Footer */}
      {!disabled && totalDetails > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Plan Details: {totalDetails}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Progress: {completedDetails}/{totalDetails}
              </div>
              <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-shoorah-primary h-2 rounded-full transition-all duration-300"
                  style={{
                    width: totalDetails > 0 ? `${(completedDetails / totalDetails) * 100}%` : '0%'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PlanDetailsManagement.propTypes = {
  title: PropTypes.string,
  planDetails: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string
};

PlanDetailsManagement.defaultProps = {
  title: 'Plan Details',
  disabled: false,
  placeholder: 'No plan details added yet'
};

export default PlanDetailsManagement;
